import { PHONE_DIAL_CODES_BY_COUNTRY } from "./data/PHONE_DIAL_CODES"
import type { Parseable } from "./parseable"

/**
 *  This is a class representing a phone number country code inside our system.
 *
 *  Note about shortcoming:
 *  - countries that share the same phone country code aren't handled correctly. US and Canada
 *  both share +1 prefix, but only US is returned by the class. This can be solved by passing
 *  the country code to the parse method.
 */
export class PhoneNumberCountryCode {
  /**
   *  Parse a string or undefined into a PhoneNumberCountryCode.
   *  Returns undefined if parsing fails.
   *
   *  @param input The input string to parse should be a dial code
   *  @param countryCode The country code to parse (optional). This parameter should be used to force a specific country code matching the dial code.
   *
   */
  static parse(
    input: PhoneNumberCountryCode | string | undefined,
    countryCode?: string
  ): PhoneNumberCountryCode | undefined {
    if (input instanceof PhoneNumberCountryCode) {
      return input
    }

    if (!input) {
      return undefined
    }

    try {
      return new PhoneNumberCountryCode(input, countryCode)
    } catch {
      return undefined
    }
  }

  /**
   *  Seek country codes based on input.
   *  Can search by prefix (numeric), country code (2-letter), or country name (partial match).
   *
   * @param input The search input (prefix digits or country code).
   *
   * @returns Array of matching PhoneNumberCountryCode instances.
   */
  static seek(input: string | undefined): readonly PhoneNumberCountryCode[] {
    let searchTerm = (input || "").replace(/[+\s]/g, "").trim().toUpperCase()

    if (!searchTerm?.length) {
      return []
    }

    // Normalize the search term if it starts with 00 or 0
    if (searchTerm.startsWith("00")) {
      searchTerm = "+" + searchTerm.substring(2)
    } else if (searchTerm.startsWith("0")) {
      searchTerm = "+" + searchTerm.substring(1)
    }

    return phoneCountryCodes.filter((code) => code.startsWith(searchTerm))
  }

  /**
   *  Get access to all supported phone number country codes.
   */
  static get allSupported(): ReadonlyArray<PhoneNumberCountryCode> {
    return phoneCountryCodes
  }

  /**
   *  The value of the country code. It's in the E.164 format (like +4, +31, +123)
   */
  private _value: string

  /**
   *  The number of the country without the "+" (like 4, 31, 123).
   *  This is convenient for search comparisons
   */
  private _number: number

  /**
   * The country code (like UK, NK, US, etc)
   */
  private _countryCode: string

  /**
   * The prefix of the country code.
   */
  get prefix() {
    return this._value
  }

  /**
   *  The country of the country code (like UK, NK, US, etc)
   */
  get countryCode() {
    return this._countryCode
  }

  /**
   *  Constructor based on a perfect string. The string should be in the E.164
   *  format (like +4, +31, +123)
   */
  constructor(input: string, countryCode?: string) {
    const found = PHONE_DIAL_CODES_BY_COUNTRY.find((code) => {
      // if we don't have a country code, we can just check if the dial code is the same
      if (countryCode) {
        return code.dial_code === input && code.code === countryCode
      }

      return code.dial_code === input
    })

    if (!found) {
      throw new Error(`Invalid country code: ${input}`)
    }

    // assign the values
    this._value = found.dial_code
    this._number = parseInt(found.dial_code.replace(/[+\s]/g, ""), 10)
    this._countryCode = found.code
  }

  /**
   *  Get a string representation of the country code prefix in +XXX notation.
   */
  toString() {
    return this._value
  }

  /**
   * Returns whether the phone number dial code or country code starts with the
   * given input.
   */
  startsWith(input: string): boolean {
    return (
      this._value.startsWith(input) || this._number.toString().startsWith(input) || this._countryCode.startsWith(input)
    )
  }
}

// Create a readonly array of the parsed phone number country codes.
// it's a small optimization to avoid parsing the same country code multiple times.
const phoneCountryCodes: ReadonlyArray<PhoneNumberCountryCode> = PHONE_DIAL_CODES_BY_COUNTRY.map(
  (code) => new PhoneNumberCountryCode(code.dial_code, code.code)
)

export { phoneCountryCodes }

const _parseable: Parseable<PhoneNumberCountryCode> = PhoneNumberCountryCode
void _parseable
